import React from 'react';
import { Sparkles, Palette, Zap, Building2, Heart, Droplets, Flower2 } from 'lucide-react';
import { PROFESSIONAL_PROFESSIONS } from '@/lib/professions';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

const iconMap: Record<string, any> = {
  lash: Sparkles,
  esthe: Palette,
  brow: Zap,
  wax: Droplets,
  spa: Flower2,
  studio: Building2,
  client: Heart,
};

const roles = [
  ...PROFESSIONAL_PROFESSIONS.filter((p) =>
    ['lash', 'esthe', 'brow', 'wax', 'spa', 'studio'].includes(p.id)
  ).map((p) => ({
    label: p.brandLabel,
    color: p.color,
    desc: `For ${p.specialty.toLowerCase()}s`,
    icon: iconMap[p.id] || Sparkles,
  })),
  { label: 'Clients', color: '#ef4444', desc: 'Find your artist', icon: Heart },
];

export default function AuthBrandPanel() {
  return (
    <div
      className="hidden lg:flex lg:w-[52%] xl:w-[55%] relative flex-col justify-between p-12 xl:p-16 overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #1a1a2e 0%, #2d2d44 50%, #1e1a3a 100%)' }}
    >
      <div className="blob-primary absolute top-20 left-20 w-[500px] h-[500px] opacity-30 pointer-events-none" />
      <div className="blob-mint absolute bottom-20 right-0 w-[400px] h-[400px] opacity-20 pointer-events-none" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(rgba(139,124,248,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(139,124,248,0.04) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-16">
          <AppImage
            src="/assets/images/file_00000000049881f4ba7bdc2dc261021b-1786785034842.png"
            alt="AVEN Logo"
            width={40}
            height={40}
            className="flex-shrink-0 object-contain"
            priority={true}
          />
          <span className="font-black text-2xl tracking-tight text-white">AvenLink</span>
        </div>

        <div className="mb-12">
          <h1
            className="font-black leading-tight mb-5 text-white"
            style={{ fontSize: 'clamp(2rem, 3.2vw, 3rem)' }}
          >
            The operating system
            <br />
            for beauty professionals.
          </h1>
          <p className="text-white/60 text-base leading-relaxed max-w-sm">
            Technology that quietly handles the business while you focus on the craft.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-12">
          {roles.map((r) => {
            const Icon = r.icon;
            return (
              <div
                key={r.label}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{
                  background: `${r.color}15`,
                  border: `1px solid ${r.color}30`,
                  color: r.color,
                }}
              >
                <Icon size={11} />
                {r.label}
              </div>
            );
          })}
        </div>

        <div
          className="p-4 rounded-2xl"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <p className="text-sm text-white/70 leading-relaxed">
            Bookings, CRM, discovery, and payments — built for lash, brow, wax, spa, and studio
            professionals.
          </p>
        </div>
      </div>

      <div className="relative z-10 text-xs text-white/40">
        AvenLink — beauty business infrastructure
      </div>
    </div>
  );
}
